import React, { useState, useEffect } from 'react';
import { FACILITIES } from '../../hooks/facilityConfig';
import './UsageAnalyticsDashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://mansion-facility-wtr-reservation-api.info1499.workers.dev';

const UsageAnalyticsDashboard = () => {
  const [selectedFacilityId, setSelectedFacilityId] = useState('all');
  const [timeRange, setTimeRange] = useState(30);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch analytics data
  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const endpoint = selectedFacilityId === 'all' 
        ? `${API_BASE_URL}/api/v1/analytics/usage?facility=all&days=${timeRange}`
        : `${API_BASE_URL}/api/v1/analytics/usage?facility=${selectedFacilityId}&days=${timeRange}`;
        
      const response = await fetch(endpoint);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch analytics: ${response.status}`);
      }
      
      const data = await response.json();
      setAnalytics(data.analytics);
    } catch (err) {
      setError(err.message);
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on mount and when filters change
  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFacilityId, timeRange]);

  // Export data as CSV
  const exportData = async (format = 'csv') => {
    if (!selectedFacilityId || selectedFacilityId === 'all') {
      alert('CSVエクスポートには特定の施設を選択してください');
      return;
    }
    
    const endDate = new Date().toISOString().split('T')[0];
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - timeRange);
    const startDateStr = startDate.toISOString().split('T')[0];
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/analytics/export?facility=${selectedFacilityId}&format=${format}&startDate=${startDateStr}&endDate=${endDate}`
      );
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `facility-${selectedFacilityId}-analytics-${startDateStr}-${endDate}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export error:', err);
      alert('データのエクスポートに失敗しました');
    }
  };

  // Format percentage
  const formatPercentage = (value) => {
    if (value == null || isNaN(value)) return '0%';
    return `${(value * 100).toFixed(1)}%`;
  };

  // Get trend indicator
  const getTrendIndicator = (direction, changeRate) => {
    if (!direction) return '';
    
    const icon = direction === 'increasing' ? '📈' : 
                 direction === 'decreasing' ? '📉' : '➡️';
    const color = direction === 'increasing' ? 'trend-up' : 
                  direction === 'decreasing' ? 'trend-down' : 'trend-stable';
    
    return (
      <span className={`trend-indicator ${color}`}>
        {icon} {Math.abs(changeRate * 100).toFixed(1)}%
      </span>
    );
  };

  // Render single facility analytics
  const renderFacilityAnalytics = (facilityId, data) => {
    if (!data) return null;
    
    const facility = FACILITIES[facilityId];
    if (!facility) return null;
    
    return (
      <div key={facilityId} className="facility-analytics-card">
        <h3>{facility.icon} {facility.name}</h3>
        
        <div className="analytics-metrics">
          <div className="metric-card">
            <div className="metric-label">平均稼働率</div>
            <div className="metric-value">
              {formatPercentage(data.averageOccupancy)}
            </div>
            {data.trends && getTrendIndicator(data.trends.direction, data.trends.changeRate)}
          </div>
          
          <div className="metric-card">
            <div className="metric-label">平日 vs 週末</div>
            <div className="metric-comparison">
              <div>平日: {formatPercentage(data.weekdayVsWeekend?.weekday)}</div>
              <div>週末: {formatPercentage(data.weekdayVsWeekend?.weekend)}</div>
            </div>
          </div>
        </div>
        
        {data.peakHours && data.peakHours.length > 0 && (
          <div className="peak-hours-section">
            <h4>ピーク利用時間</h4>
            <div className="peak-hours-list">
              {data.peakHours.slice(0, 3).map((hour, index) => (
                <div key={index} className="peak-hour-item">
                  <span className="peak-hour-time">{hour.hour}</span>
                  <span className="peak-hour-frequency">
                    {hour.frequency}日間で最多
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {data.peakDays && data.peakDays.length > 0 && (
          <div className="peak-days-section">
            <h4>最も混雑した日</h4>
            <div className="peak-days-list">
              {data.peakDays.slice(0, 3).map((day, index) => (
                <div key={index} className="peak-day-item">
                  <span className="peak-day-date">
                    {new Date(day.date).toLocaleDateString('ja-JP', {
                      month: 'short',
                      day: 'numeric'
                    })}
                  </span>
                  <span className="peak-day-occupancy">
                    {formatPercentage(day.occupancy)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {data.monthlyStats && data.monthlyStats.length > 0 && (
          <div className="monthly-stats-section">
            <h4>月別統計</h4>
            <div className="monthly-stats-chart">
              {data.monthlyStats.map((month, index) => (
                <div key={index} className="month-stat">
                  <div className="month-label">
                    {new Date(month.month + '-01').toLocaleDateString('ja-JP', { 
                      year: 'numeric', 
                      month: 'short' 
                    })}
                  </div>
                  <div className="month-bar-container">
                    <div 
                      className="month-bar"
                      style={{ width: `${month.occupancy * 100}%` }}
                    />
                    <span className="month-value">
                      {formatPercentage(month.occupancy)}
                    </span>
                  </div>
                  <div className="month-bookings">
                    {month.totalBookings} 予約
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="usage-analytics-dashboard">
      <div className="analytics-header">
        <h2>施設利用状況分析</h2>
        
        <div className="analytics-controls">
          <div className="control-group">
            <label htmlFor="facility-select">施設:</label>
            <select
              id="facility-select"
              value={selectedFacilityId}
              onChange={(e) => setSelectedFacilityId(e.target.value)}
              className="facility-select"
            >
              <option value="all">全施設</option>
              {Object.values(FACILITIES).map(f => (
                <option key={f.id} value={f.id}>
                  {f.icon} {f.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="control-group">
            <label htmlFor="time-range">期間:</label>
            <select
              id="time-range"
              value={timeRange}
              onChange={(e) => setTimeRange(parseInt(e.target.value))}
              className="time-range-select"
            >
              <option value={7}>過去7日間</option>
              <option value={30}>過去30日間</option>
              <option value={90}>過去90日間</option>
              <option value={365}>過去1年間</option>
            </select>
          </div>
          
          <div className="control-group">
            <button 
              onClick={() => fetchAnalytics()}
              className="refresh-button"
              disabled={loading}
            >
              🔄 更新
            </button>
            
            {selectedFacilityId !== 'all' && (
              <>
                <button 
                  onClick={() => exportData('csv')}
                  className="export-button"
                  disabled={loading}
                >
                  📥 CSV
                </button>
                <button 
                  onClick={() => exportData('json')}
                  className="export-button"
                  disabled={loading}
                >
                  📥 JSON
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      
      {loading && (
        <div className="loading-state">
          <div className="loading-spinner">分析データを読み込み中...</div>
        </div>
      )}
      
      {error && (
        <div className="error-state">
          <p>エラーが発生しました: {error}</p>
          <button onClick={fetchAnalytics} className="retry-button">
            再試行
          </button>
        </div>
      )}
      
      {!loading && !error && analytics && (
        <div className="analytics-content">
          {selectedFacilityId === 'all' ? (
            // Show all facilities
            <div className="all-facilities-grid">
              {Object.entries(analytics).map(([facilityId, data]) => 
                renderFacilityAnalytics(facilityId, data)
              )}
            </div>
          ) : (
            // Show single facility
            renderFacilityAnalytics(selectedFacilityId, analytics)
          )}
        </div>
      )}
      
      {!loading && !error && !analytics && (
        <div className="no-data-state">
          <p>分析データがありません。</p>
          <p>施設の利用が記録されると、ここに統計が表示されます。</p>
        </div>
      )}
    </div>
  );
};

export default UsageAnalyticsDashboard;